import { capitalize, compact, uniqBy } from "lodash";
import { useMemo, useState } from "react";
import { AskAiChatMessageFragmentFragment } from "types/graphql-schema";

import IntegrationLogo, {
  IntegrationLogoType,
} from "@components/integration-logo/integration-logo";
import AppLink from "@components/link/link";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { classNames } from "@helpers/css";
import { pluralize, titleCase } from "@helpers/string";

type AskAICitationType = NonNullable<
  NonNullable<
    NonNullable<
      NonNullable<
        NonNullable<AskAiChatMessageFragmentFragment["outputItems"]>[number]
      >["suggestedAssessmentResponses"]
    >[number]
  >["citations"]
>;

const AskAICitationModal = ({
  citations,
  onClose,
}: {
  citations: AskAICitationType;
  onClose: () => void;
}) => {
  const [filteredType, setFilteredType] = useState<string | null>(null);
  const compactedCitations = useMemo(() => compact(citations), [citations]);
  const citationsWithUniqueSourceProviders = useMemo(
    () =>
      uniqBy(compactedCitations, "sourceProvider").filter(
        (type) => type.sourceProvider
      ),
    [compactedCitations]
  );
  const filteredCitations = useMemo(
    () =>
      compactedCitations.filter((citation) =>
        filteredType ? citation.sourceProvider === filteredType : true
      ),
    [compactedCitations, filteredType]
  );

  const filterTypeClassName =
    "border px-3 py-1 rounded-full text-gray-500 flex items-center gap-1";
  const filterTypeClassNameActive = "border-blue-100 bg-blue-50";

  return (
    <Modal alignedTop open>
      <div className="p-6 flex flex-col gap-6">
        <ModalTitle onClose={onClose}>
          {compactedCitations.length}{" "}
          {pluralize("source", compactedCitations.length)} Used
        </ModalTitle>
        {citationsWithUniqueSourceProviders.length > 0 && (
          <div className="flex gap-2">
            <button
              className={classNames(
                filterTypeClassName,
                filteredType === null ? filterTypeClassNameActive : ""
              )}
              onClick={() => setFilteredType(null)}
            >
              All
            </button>
            {citationsWithUniqueSourceProviders.map((sourceProvider) => (
              <button
                key={sourceProvider.sourceProvider}
                className={classNames(
                  filterTypeClassName,
                  filteredType === sourceProvider.sourceProvider
                    ? filterTypeClassNameActive
                    : ""
                )}
                onClick={() =>
                  setFilteredType(sourceProvider.sourceProvider || null)
                }
              >
                {sourceProvider.sourceProvider ? (
                  <IntegrationLogo
                    type={
                      capitalize(
                        sourceProvider.sourceProvider
                      ) as IntegrationLogoType
                    }
                    className="mt-0.5 w-4 h-4"
                  />
                ) : (
                  <TopicflowIcon
                    type="externalLink"
                    className="mt-0.5 w-4 h-4"
                  />
                )}
                {titleCase(sourceProvider.sourceProvider)}
              </button>
            ))}
          </div>
        )}
        <div className="flex flex-col gap-4">
          {filteredCitations.map((citation) => (
            <div key={citation.url} className="flex items-center">
              {citation.sourceProvider ? (
                <IntegrationLogo
                  type={
                    capitalize(citation.sourceProvider) as IntegrationLogoType
                  }
                  className="float-left mr-1.5 mt-0.5 w-4 h-4"
                />
              ) : (
                <TopicflowIcon
                  type="externalLink"
                  className="float-left mr-1.5 mt-0.5 w-4 h-4"
                />
              )}
              <AppLink
                to={citation.url}
                className="text-gray-700 hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                {citation.title}
              </AppLink>
            </div>
          ))}
          {filteredCitations.length === 0 && (
            <div className="text-gray-500">No citations found</div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AskAICitationModal;
